import type { EquipmentPurpose } from '@/types/equipment'
import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { equipmentApi, type ApiOccurrence } from '@/api/equipmentApi'
import { useMockApi } from '@/lib/useMock'
import { useTenant } from '@/providers/TenantProvider'

/** Visible calendar range, as reported by the planner calendar ([start, end)). */
export type OccurrenceRange = { start: string; end: string }

/**
 * Occurrences for the selected tenant, expanded server-side by the recurrence
 * engine for a visible calendar range. Refetches whenever the range (or optional
 * group filter) changes. Disabled until both a tenant and a range are known.
 */
export function useOccurrences(
  range: OccurrenceRange | null,
  groupId?: string,
  /** Only equipment of these purposes (z8nrz7ewt2). Omitted: every purpose. */
  purposes?: readonly EquipmentPurpose[],
): { data: ApiOccurrence[]; isLoading: boolean; error: string | null } {
  const { selectedTenantId } = useTenant()
  const tenantId = useMockApi ? 'mock' : selectedTenantId

  const queryKey = useMemo(
    () => ['equipment-occurrences', tenantId, range?.start, range?.end, groupId, purposes?.join(',') ?? 'all'] as const,
    [tenantId, range?.start, range?.end, groupId, purposes],
  )

  const query = useQuery({
    queryKey,
    enabled: tenantId !== null && range !== null && !useMockApi,
    queryFn: async (): Promise<ApiOccurrence[]> => {
      if (!range) return []
      return equipmentApi.listOccurrences(tenantId as string, {
        from: range.start,
        to: range.end,
        groupId,
        purposes,
      })
    },
  })

  return {
    data: query.data ?? [],
    isLoading: query.isLoading && query.fetchStatus !== 'idle',
    error: query.error instanceof Error ? query.error.message : null,
  }
}
